import { Injectable } from '@angular/core';

/**
 * Shared client-side image compression, used by every image upload field in
 * the app (uic-form's 'image' UIC fields, item-master's item image gallery,
 * and anywhere else that needs it) instead of each place reinventing its own
 * version. Downscales + re-encodes in-browser via Canvas before the file
 * ever hits an upload API call - no server round trip needed to shrink it.
 *
 * Same approach as ImageOptimizerService in @advinsoftwares/ngx-rich-text-editor
 * (used by the CRM lead-details rich text editor), deliberately WITHOUT its
 * WebP re-encoding: images uploaded through this app's forms (item photos,
 * logos, employee photos) get downloaded and shared outside this app far
 * more than the rich text editor's inline content does - WhatsApp, email,
 * Word, a random viewer on someone's PC - so this sticks to JPEG/PNG, which
 * every tool anywhere opens without a second thought.
 *
 * - Caps the longest edge at maxDim (1920px) before re-encoding - plenty for
 *   anything this app displays, while phone camera photos routinely come in
 *   at 3000-4000px+.
 * - Targets a max file size (targetBytes, default 300KB): tries JPEG at
 *   photoQuality first, and if that's still over budget, steps quality down
 *   (0.75 -> 0.65 -> 0.55 -> minQuality) before finally shrinking resolution
 *   further (in ~15% steps, down to a 480px floor) and retrying at
 *   minQuality. Every attempt is kept as a candidate; whichever one comes
 *   out smallest wins, so a genuinely hard image (busy photo, huge source)
 *   still gets the best size/quality tradeoff available rather than an
 *   all-or-nothing pass/fail.
 * - Detects real transparency (any sampled pixel with alpha below ~98%) and
 *   only considers PNG for those - JPEG has no alpha channel, so re-encoding
 *   a logo with a transparent background to JPEG would bake in a black
 *   backdrop. Everything else is free to go through the JPEG/size-ladder
 *   path above, including images that a naive "flat graphic" color-count
 *   heuristic would mis-tag (e.g. a mostly-black product photo with a
 *   smooth marble/gradient texture can look like a flat logo at a 24x24
 *   sample, even though it's really a photo that JPEG handles far better
 *   than a lossless re-encode ever could).
 * - Also tries lossless PNG for images that look like flat graphics (few
 *   unique colors at a 24x24 sample - logos, UI screenshots, icons), since
 *   exact colors matter more than raw size there. Whichever candidate
 *   (PNG or JPEG) comes out smallest wins.
 * - Never regresses size: if nothing beats the original file's size, the
 *   original is kept as-is.
 * - Never throws: any failure (corrupt image, decode error, no canvas
 *   context) resolves to the original file rather than blocking the upload.
 * - SVG and animated GIF are never touched - rasterizing an SVG defeats its
 *   whole point, and canvas re-encoding would flatten a GIF to one frame.
 */
@Injectable({
  providedIn: 'root'
})
export class ImageCompressionService {

  // Logs to the console deliberately (debug on the normal paths, warn on
  // failure) instead of failing silently. This method is designed to NEVER
  // throw and NEVER block an upload - which also means that without some
  // visible trace, a silent failure (a strict CSP blocking blob:/canvas
  // operations, a privacy extension patching canvas APIs, an old browser
  // missing createImageBitmap) is completely invisible: the file just
  // quietly goes through uncompressed and looks, from the outside, exactly
  // like "compression didn't help this file". Open DevTools console on any
  // upload to see which of these actually happened.
  async compressImage(
    file: File,
    maxDim = 1920,
    photoQuality = 0.85,
    targetBytes = 300 * 1024,
    minQuality = 0.5
  ): Promise<File> {
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml' || file.type === 'image/gif') {
      console.debug(`[ImageCompressionService] Skipped ${file.name}: not a compressible image type (${file.type || 'unknown'}).`);
      return file;
    }

    let bitmap: ImageBitmap | null = null;
    try {
      bitmap = await createImageBitmap(file);
      let width: number, height: number;
      ({ width, height } = this.computeTargetSize(bitmap.width, bitmap.height, maxDim));

      const drawAt = (w: number, h: number): CanvasRenderingContext2D | null => {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx || !bitmap) {
          return null;
        }
        ctx.drawImage(bitmap, 0, 0, w, h);
        return ctx;
      };

      let ctx = drawAt(width, height);
      if (!ctx) {
        console.warn(`[ImageCompressionService] Canvas 2D context unavailable for ${file.name} - uploading original, uncompressed.`);
        return file;
      }

      const { isFlatGraphic, hasTransparency } = this.analyzeImage(ctx, width, height);

      // Every successful encode attempt is kept here rather than tracked
      // via a running "best so far" variable - a `let` reassigned from
      // inside an async closure defeats TypeScript's narrowing (it can't
      // prove what a called function did to a captured variable), so
      // comparisons are done by scanning this array instead.
      type Candidate = { blob: Blob; format: 'png' | 'jpeg' };
      const candidates: Candidate[] = [];

      const tryEncode = async (type: 'image/png' | 'image/jpeg', quality?: number): Promise<void> => {
        const blob = await this.canvasToBlob(ctx!.canvas, type, quality);
        if (blob) {
          candidates.push({ blob, format: type === 'image/png' ? 'png' : 'jpeg' });
        }
      };

      const smallestSoFar = (): Candidate | null =>
        candidates.reduce<Candidate | null>((min, c) => (!min || c.blob.size < min.blob.size) ? c : min, null);

      if (isFlatGraphic || hasTransparency) {
        await tryEncode('image/png');
      }

      if (!hasTransparency) {
        const qualitySteps = Array.from(new Set([photoQuality, 0.75, 0.65, 0.55, minQuality]))
          .filter(q => q > 0 && q <= 1)
          .sort((a, b) => b - a);

        for (const q of qualitySteps) {
          await tryEncode('image/jpeg', q);
          const lastAttempt = candidates[candidates.length - 1];
          if (lastAttempt && lastAttempt.format === 'jpeg' && lastAttempt.blob.size <= targetBytes) {
            break;
          }
        }

        // Still over budget at minQuality? Shrink resolution further and
        // retry - re-drawn from the original bitmap each time (not from an
        // already lossy canvas), so quality degrades as little as possible
        // for whatever final size we end up needing.
        const minLongestEdge = 480;
        let attempt = 0;
        while (
          (smallestSoFar()?.blob.size ?? Infinity) > targetBytes &&
          attempt < 4 &&
          Math.max(width, height) > minLongestEdge
        ) {
          width = Math.max(minLongestEdge, Math.round(width * 0.85));
          height = Math.max(minLongestEdge, Math.round(height * 0.85));
          const smallerCtx = drawAt(width, height);
          if (!smallerCtx) {
            break;
          }
          ctx = smallerCtx;
          await tryEncode('image/jpeg', minQuality);
          attempt++;
        }
      }

      const best = smallestSoFar();

      if (!best) {
        console.warn(`[ImageCompressionService] canvas.toBlob() returned null for every attempt on ${file.name} - uploading original, uncompressed. This usually means a strict Content-Security-Policy or browser extension is blocking canvas encoding.`);
        return file;
      }

      if (best.blob.size >= file.size) {
        console.debug(`[ImageCompressionService] ${file.name}: best re-encode (${best.blob.size}B) wasn't smaller than the original (${file.size}B) - keeping the original.`);
        return file; // nothing we tried actually helped - never regress size
      }

      const compressed = new File([best.blob], this.withExtension(file.name, best.blob.type), { type: best.blob.type });
      const label = best.format === 'png' ? 'PNG' : 'JPEG';
      const overBudgetNote = compressed.size > targetBytes
        ? ` (still above the ${Math.round(targetBytes / 1024)}KB target - image stayed complex even at minimum quality/size)`
        : '';
      console.debug(`[ImageCompressionService] ${file.name}: ${file.size}B -> ${compressed.size}B (${label})${overBudgetNote}.`);
      return compressed;
    } catch (err) {
      console.warn(`[ImageCompressionService] Compression failed for ${file.name} - uploading original, uncompressed.`, err);
      return file;
    } finally {
      bitmap?.close();
    }
  }

  private computeTargetSize(width: number, height: number, maxDimension: number): { width: number; height: number } {
    const longest = Math.max(width, height);
    if (longest <= maxDimension) {
      return { width, height };
    }
    const scale = maxDimension / longest;
    return { width: Math.round(width * scale), height: Math.round(height * scale) };
  }

  // Cheap heuristics, not a real classifier - sampled down to 24x24:
  // - isFlatGraphic: counts distinct (quantized) colors. Photos retain
  //   hundreds of unique colors even at that resolution; flat-color
  //   graphics (logos, UI screenshots, icons) stay under a few dozen. This
  //   can mis-tag a mostly-one-color photo as "flat" - that's fine, because
  //   the caller tries JPEG regardless and just keeps whichever result is
  //   smaller.
  // - hasTransparency: true if any sampled pixel is meaningfully
  //   non-opaque. JPEG can't represent alpha, so this gates PNG-only
  //   handling for anything that actually needs a transparent background.
  private analyzeImage(ctx: CanvasRenderingContext2D, width: number, height: number): { isFlatGraphic: boolean; hasTransparency: boolean } {
    const sampleSize = 24;
    const uniqueColorThreshold = 48;
    const opaqueAlphaFloor = 250; // out of 255 - allows for harmless anti-aliasing noise
    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = sampleSize;
    sampleCanvas.height = sampleSize;
    const sampleCtx = sampleCanvas.getContext('2d');
    if (!sampleCtx) {
      return { isFlatGraphic: false, hasTransparency: false };
    }
    sampleCtx.drawImage(ctx.canvas, 0, 0, width, height, 0, 0, sampleSize, sampleSize);
    const { data } = sampleCtx.getImageData(0, 0, sampleSize, sampleSize);
    const seen = new Set<number>();
    let isFlatGraphic = true;
    let hasTransparency = false;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < opaqueAlphaFloor) {
        hasTransparency = true;
      }
      if (isFlatGraphic) {
        // Quantize slightly so near-identical anti-aliased edge pixels don't inflate the count.
        const key = (Math.round(data[i] / 8) << 16) | (Math.round(data[i + 1] / 8) << 8) | Math.round(data[i + 2] / 8);
        seen.add(key);
        if (seen.size > uniqueColorThreshold) {
          isFlatGraphic = false;
        }
      }
    }
    return { isFlatGraphic, hasTransparency };
  }

  private canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), type, quality));
  }

  private withExtension(originalName: string, contentType: string): string {
    const base = originalName.replace(/\.[a-z0-9]+$/i, '');
    const ext = contentType === 'image/png' ? 'png' : 'jpg';
    return `${base}.${ext}`;
  }
}
